import type { PaymentMethod, SalePayment } from './types';

/*
 * How a payment is composed, and where it is sent.
 *
 * Extracted from the till because there is now exactly one payment screen and
 * two things it can be paying for — a walk-in basket and a table's bill — and
 * the rule that they are paid the SAME WAY is the whole point. A table used to
 * be settled by two buttons that posted a method name, so «کارتخوان» there
 * recorded a card payment no reader had ever been asked to take.
 */

/** Where a settle is posted. A table's bill has its own endpoint; a basket does not. */
export function settlePath(checkId: string): string {
  return `/floor/checks/${encodeURIComponent(checkId)}/settle`;
}

/*
 * The client reference, which is what the double-charge guard blocks on.
 *
 * A table's is derived from the check, so a reader asked twice for the same
 * table is recognised as the same charge and a settle retried after a timeout
 * finds the first invoice instead of issuing the table a second one.
 */
export function checkRef(checkId: string): string {
  return `chk:${checkId}`;
}

/*
 * The payments to record, itemised.
 *
 * Cash tendered above the total is change, not revenue: the invoice is settled
 * for exactly its own value and the difference goes back in notes. The amount
 * is never the amount TYPED — it is the amount owed.
 */
export function composePayments(
  method: PaymentMethod | 'split',
  totalRial: number,
  splitCashRial: number,
  reference: string,
): SalePayment[] {
  const totalToman = Math.round(totalRial / 10);
  if (method === 'split') {
    const cashToman = Math.round(splitCashRial / 10);
    const cardToman = totalToman - cashToman;
    const out: SalePayment[] = [];
    if (cashToman > 0) out.push({ method: 'cash', amountToman: cashToman });
    if (cardToman > 0) out.push({ method: 'card', amountToman: cardToman, rrn: reference || undefined });
    return out;
  }
  return [{ method, amountToman: totalToman, rrn: reference || undefined }];
}

/*
 * A bill, as the agent describes one.
 *
 * Structural rather than imported so this module stays free of the screens
 * that use it. Only the fields a settle needs are named.
 */
export interface Bill {
  id: string;
  number: string;
  tableLabel?: string;
  totalRial: number;
  /*
   * What the agent will actually charge, which is not always totalRial — the
   * bill excludes the VAT the invoice adds. Absent from an older agent, where
   * totalRial is the only figure there is.
   */
  payableRial?: number;
  rounds: { status: string; lines: { name: string; quantityMilli: number; lineTotalRial: number }[] }[];
}

/** What the payment screen is handed when it is settling a table. */
export interface SettleOrder {
  checkId: string;
  number: string;
  tableLabel: string;
  totalRial: number;
  lines: { name: string; quantity: number; lineTotalRial: number }[];
}

/*
 * settleOrderFrom turns a bill into what the payment screen settles.
 *
 * One definition, used by the tables screen and by an order opened from
 * «سفارش‌های ورودی». There are two ways into payment for the same table now,
 * and the amount must not depend on which one a cashier took.
 *
 * Every non-voided round, never one of them. An arrival is a single round, so
 * the obvious shortcut — settle the round the cashier tapped — charges a table
 * that ordered twice for one round, and the figure looks perfectly reasonable
 * on screen. The agent recomputes the bill and refuses a settle that pays
 * less, so the damage would be a refused payment rather than a lost one; but
 * the till should not be proposing the wrong number in the first place.
 */
export function settleOrderFrom(bill: Bill): SettleOrder {
  return {
    checkId: bill.id,
    number: bill.number,
    tableLabel: bill.tableLabel ?? '',
    totalRial: bill.payableRial ?? bill.totalRial,
    lines: bill.rounds
      .filter((r) => r.status !== 'voided')
      .flatMap((r) => r.lines.map((l) => ({
        name: l.name,
        quantity: l.quantityMilli / 1000,
        lineTotalRial: l.lineTotalRial,
      }))),
  };
}
